import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ResponsabileLayout } from "./ResponsabileLayout";
import { OrdineForm } from "@/components/OrdineForm";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2, Search } from "lucide-react";

const eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const STATO_PROD = { raccolta_ordini: "Raccolta ordini", ordini_chiusi: "Ordini chiusi", in_preparazione: "In preparazione", in_produzione: "In produzione", pronto: "Pronto", consegnato: "Consegnato" };
const STATO_PAG = { da_pagare: { l: "Da pagare", c: "bg-red-400/15 text-red-300 border-red-400/30" }, acconto: { l: "Acconto", c: "bg-amber-400/15 text-amber-300 border-amber-400/30" }, saldato: { l: "Saldato", c: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" } };
const SEZ = [["riepilogo", "Riepilogo"], ["nuovo", "Nuovo ordine"], ["ordini", "Ordini"], ["pagamenti", "Pagamenti"], ["versamenti", "Versamenti"], ["stato", "Stato"]];

export default function PortaleRaccolta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sez, setSez] = useState("riepilogo");

  const load = useCallback(() => {
    api.get(`/raccolta/portale/raccolte/${id}`).then(({ data }) => setR(data)).catch(() => toast.error("Raccolta non trovata")).finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  if (loading || !r) return <ResponsabileLayout><div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</div></ResponsabileLayout>;

  const prodottiAttivi = (r.prodotti || []).filter((p) => p.attivo);
  const chiusa = r.stato_ordini === "chiusa";
  const programmata = r.stato_ordini === "programmata";
  const puoInserire = r.stato_ordini === "aperta" || (chiusa && r.slot_disponibili > 0);

  return (
    <ResponsabileLayout>
      <button onClick={() => navigate("/responsabile")} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-3"><ArrowLeft className="w-4 h-4" /> Le mie raccolte</button>
      <div className="glass rounded-2xl p-4 mb-4">
        <div className="text-xl font-bold text-slate-100">{r.nome_gruppo}</div>
        <div className="text-xs text-slate-500">{r.tipologia_gruppo} · {r.festa} · {r.edizione}</div>
        {programmata && (
          <div className="mt-3 bg-slate-900/40 rounded-lg p-3 text-sm">
            <div className="text-slate-300 font-semibold">Apertura ordini</div>
            <div className="text-slate-400">{(r.data_ora_apertura || "").replace("T", " ").slice(0, 16)}</div>
            <div className="text-slate-300 font-semibold mt-2">Chiusura ordini</div>
            <div className="text-slate-400">{(r.data_ora_chiusura || "").replace("T", " ").slice(0, 16)}</div>
          </div>
        )}
        {chiusa && r.slot_disponibili > 0 && (
          <div className="mt-3 bg-orange-400/10 border border-orange-400/30 rounded-lg p-3 text-sm text-orange-200" data-testid="slot-banner">
            Gigli Edition ha autorizzato <b>{r.slot_disponibili}</b> ordine/i aggiuntivo/i.
          </div>
        )}
        {chiusa && r.slot_disponibili === 0 && (
          <div className="mt-3 text-xs text-amber-300/80">Dopo la scadenza non è più possibile aggiungere o modificare ordini salvo autorizzazione di Gigli Edition.</div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {SEZ.map(([k, l]) => (
          <button key={k} data-testid={`sez-${k}`} onClick={() => setSez(k)} className={`px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap border ${sez === k ? "bg-amber-400/15 text-amber-300 border-amber-400/40" : "text-slate-400 border-transparent hover:bg-slate-800"}`}>{l}</button>
        ))}
      </div>

      {sez === "riepilogo" && <Riepilogo r={r} />}
      {sez === "nuovo" && (
        puoInserire ? (
          prodottiAttivi.length === 0 ? <p className="text-slate-500 text-sm" data-testid="nuovo-no-prodotti">La raccolta non ha ancora articoli disponibili. Contatta Gigli Edition.</p> :
          <div className="glass rounded-2xl p-4">
            <OrdineForm prodotti={prodottiAttivi} allowSaveNew onSubmit={async (payload, andNew) => {
              try {
                await api.post(`/raccolta/portale/raccolte/${id}/ordini`, payload);
                toast.success("Ordine salvato");
                load();
                if (!andNew) setSez("ordini");
              }
              catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
            }} />
          </div>
        ) : (
          <div className="glass rounded-2xl p-4 text-center text-slate-400" data-testid="nuovo-bloccato">
            {programmata ? "La raccolta non è ancora aperta." : "Raccolta chiusa. Nessun ordine aggiuntivo autorizzato."}
          </div>
        )
      )}
      {sez === "ordini" && <Ordini raccolta={r} onChanged={load} puoModificare={r.stato_ordini === "aperta"} />}
      {sez === "pagamenti" && <Pagamenti raccolta={r} onChanged={load} />}
      {sez === "versamenti" && <Versamenti raccolta={r} onChanged={load} />}
      {sez === "stato" && <StatoProduzione r={r} />}
    </ResponsabileLayout>
  );
}

function Riepilogo({ r }) {
  const kpi = [["Ordini", r.num_ordini, "text-slate-200"], ["Valore ordini", eur(r.valore_ordini), "text-emerald-300"], ["Incassato clienti", eur(r.incassato), "text-cyan-300"], ["Da incassare", eur(r.da_incassare), "text-amber-300"], ["Consegnato a Gigli", eur(r.consegnato), "text-sky-300"], ["Saldo da consegnare", eur(r.saldo_da_consegnare), "text-violet-300"]];
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="portale-riepilogo">
      {kpi.map(([l, v, c]) => (
        <div key={l} className="glass rounded-2xl p-4 text-center"><div className={`text-2xl font-bold ${c}`}>{v}</div><div className="text-[11px] uppercase text-slate-500 mt-1">{l}</div></div>
      ))}
    </div>
  );
}

function Ordini({ raccolta, onChanged, puoModificare }) {
  const [data, setData] = useState({ items: [] });
  const [search, setSearch] = useState("");
  const [stato, setStato] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (search) p.append("search", search); if (stato) p.append("stato", stato);
    api.get(`/raccolta/portale/raccolte/${raccolta.id}/ordini?${p.toString()}`).then(({ data }) => setData({ items: data.items || [] })).catch(() => {});
  }, [raccolta.id, search, stato]);
  useEffect(load, [load]);
  const del = async (o) => {
    if (!window.confirm(`Eliminare l'ordine di ${o.nome} ${o.cognome}?`)) return;
    try { await api.delete(`/raccolta/portale/raccolte/${raccolta.id}/ordini/${o.id}`); toast.success("Eliminato"); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="portale-ordini-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca..." className="w-full pl-9 pr-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-slate-100" />
        </div>
        <select value={stato} onChange={(e) => setStato(e.target.value)} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-200">
          <option value="">Tutti</option><option value="da_pagare">Da pagare</option><option value="acconto">Acconto</option><option value="saldato">Saldato</option>
        </select>
      </div>
      {data.items.length === 0 ? <p className="text-slate-500 text-sm">Nessun ordine.</p> : (
        <div className="space-y-2" data-testid="portale-ordini-list">
          {data.items.map((o) => {
            const sp = STATO_PAG[o.stato_pagamento] || STATO_PAG.da_pagare;
            return (
              <div key={o.id} className="glass rounded-2xl p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-100">{o.nome} {o.cognome}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${sp.c}`}>{sp.l}</span>
                </div>
                <div className="text-xs text-slate-500">{o.cellulare}</div>
                <div className="text-sm text-slate-400 mt-1">{(o.righe || []).map((rg) => <div key={rg.id}>{rg.quantita}× {rg.nome_prodotto_snapshot}{rg.taglia ? ` · ${rg.taglia}` : ""}{rg.nome_retro ? ` · ${rg.nome_retro}` : ""}</div>)}</div>
                <div className="flex items-center justify-between mt-2 border-t border-slate-800 pt-2 text-sm">
                  <span className="text-slate-400">Tot {eur(o.totale)} · Res {eur(o.residuo)}</span>
                  {(puoModificare || o.ordine_eccezionale) && <button onClick={() => del(o)} className="text-xs px-2 py-1 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Pagamenti({ raccolta, onChanged }) {
  const [items, setItems] = useState([]);
  const [vals, setVals] = useState({});
  const load = useCallback(() => { api.get(`/raccolta/portale/raccolte/${raccolta.id}/ordini?page_size=200`).then(({ data }) => setItems(data.items || [])).catch(() => {}); }, [raccolta.id]);
  useEffect(load, [load]);
  const save = async (o) => {
    const v = vals[o.id] ?? o.importo_incassato;
    try { await api.patch(`/raccolta/portale/raccolte/${raccolta.id}/ordini/${o.id}/pagamento`, { importo_incassato: parseFloat(v) || 0 }); toast.success("Aggiornato"); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="portale-pagamenti">
      {items.length === 0 && <p className="text-slate-500 text-sm">Nessun ordine.</p>}
      {items.map((o) => (
        <div key={o.id} className="glass rounded-2xl p-3">
          <div className="flex items-center justify-between"><span className="font-semibold text-slate-100">{o.nome} {o.cognome}</span><span className="text-xs text-slate-500">Tot {eur(o.totale)}</span></div>
          <div className="flex items-center gap-2 mt-2">
            <input data-testid={`portale-pag-${o.id}`} type="number" step="0.01" defaultValue={o.importo_incassato} onChange={(e) => setVals((s) => ({ ...s, [o.id]: e.target.value }))} className="flex-1 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
            <button onClick={() => save(o)} className="px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold text-sm">Salva</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Versamenti({ raccolta, onChanged }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ importo: "", metodo: "Contanti", nota: "" });
  const load = useCallback(() => { api.get(`/raccolta/portale/raccolte/${raccolta.id}/versamenti`).then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => {}); }, [raccolta.id]);
  useEffect(load, [load]);
  const add = async () => {
    if (!(parseFloat(form.importo) > 0)) return toast.error("Importo non valido");
    try { await api.post(`/raccolta/portale/raccolte/${raccolta.id}/versamenti`, { importo: parseFloat(form.importo), metodo: form.metodo, nota: form.nota }); toast.success("Versamento registrato"); setForm({ importo: "", metodo: "Contanti", nota: "" }); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <div className="glass rounded-2xl p-4 space-y-2">
        <div className="text-sm font-semibold text-slate-200">Registra versamento a Gigli Edition</div>
        <div className="flex gap-2">
          <input data-testid="portale-vers-importo" type="number" step="0.01" value={form.importo} onChange={(e) => setForm((f) => ({ ...f, importo: e.target.value }))} placeholder="Importo €" className="flex-1 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100" />
          <select data-testid="portale-vers-metodo" value={form.metodo} onChange={(e) => setForm((f) => ({ ...f, metodo: e.target.value }))} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100"><option>Contanti</option><option>Bonifico</option><option>Altro</option></select>
        </div>
        <input value={form.nota} onChange={(e) => setForm((f) => ({ ...f, nota: e.target.value }))} placeholder="Nota (opzionale)" className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100" />
        <button data-testid="portale-vers-add" onClick={add} className="w-full py-2.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Registra versamento</button>
      </div>
      <div className="space-y-2" data-testid="portale-versamenti-list">
        {items.map((v) => (
          <div key={v.id} className="glass rounded-2xl p-3 flex items-center justify-between">
            <span className="font-mono-tech text-emerald-300 font-bold">{eur(v.importo)}</span>
            <span className="text-slate-400 text-sm">{v.metodo} · {(v.data_versamento || "").slice(0, 10)}{v.nota ? ` · ${v.nota}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatoProduzione({ r }) {
  return (
    <div className="glass rounded-2xl p-4 space-y-3" data-testid="portale-stato">
      <div className="flex items-center justify-between"><span className="text-slate-400">Stato produzione</span><span className="text-lg font-bold text-sky-300">{STATO_PROD[r.stato_produzione]}</span></div>
      {r.data_stimata_consegna ? (
        <div className="flex items-center justify-between"><span className="text-slate-400">Consegna stimata</span><span className="text-lg font-bold text-violet-300">{r.data_stimata_consegna}</span></div>
      ) : (
        <div className="text-sm text-slate-400">Consegna prevista in <b>15 giorni lavorativi</b> dall'avvio della produzione.</div>
      )}
      <div className="text-xs text-slate-400 bg-slate-900/40 rounded-lg p-3 space-y-2">
        <p>{r.info?.tempi_jersey}</p>
        <p>{r.info?.consegna_complessiva}</p>
        <p className="text-slate-500">La data indicata è una stima.</p>
      </div>
    </div>
  );
}
